<template>
    <div>
        <v-container grid-list-xs>
            <v-sheet class="pa-5" elevation="3">
                <PageHeader>
                    <template #header>
                        <h4>{{ $t("loan-payment.heading", { providerKey: providerKey }) }}</h4>
                    </template>
                    <template #subheader>
                        {{
                            $t(`loan-payment.description.${headerDescriptionLanguage}`, {
                                termMonths: termMonths,
                                promoMonths: promotionalTermMonths,
                                payFrequency: payFrequency,
                            })
                        }}
                    </template>
                </PageHeader>
                <div v-if="displayPromoCard" id="special-promo-container" class="px-4 pb-3">
                    <div id="promo-header">
                        <SafeHtmlRenderer :content="specialOfferHeader" />
                    </div>
                    <div id="promo-description">
                        Your payment plan includes a promotion that will allow you to pay off within
                        {{ promotionalTermMonths }} months to waive all interest charges. If you'd like to set up a
                        recurring payment amount that ensures you meet the promotional terms requirements, please choose
                        the promotional payment option below. Otherwise, you can keep your minimum payment in place and
                        take advantage of your promotional offer by paying your original principal balance before the
                        promotional period ends. See promotional addendum within your loan agreement prior to activating
                        your loan.
                    </div>
                    <div id="promo-select">
                        <v-radio-group v-model="specialPromotionalSelect">
                            <v-radio :label="currentPayment" class="promo-select-label" :value="false"> </v-radio>
                            <v-radio :label="specialPromo" class="promo-select-label" :value="true"> </v-radio>
                        </v-radio-group>
                    </div>
                    <div id="promo-disclosure">
                        Don't worry, you can take advantage of your promotional offer at any point within the
                        promotional period. See promotional addendum for details
                    </div>
                </div>

                <v-row>
                    <v-col>
                        <Form :messages="messages" :validations="validations">
                            <v-tabs id="tab-container" v-model="recurringPaymentTab" grow :vertical="isVertical">
                                <v-tab id="card-tab" @click="recurringPaymentPaymentMethod = 'CARD'"> Card* </v-tab>
                                <v-tab id="account-tab" @click="recurringPaymentPaymentMethod = 'CHECKING'">
                                    Checking Account*
                                </v-tab>
                                <v-tab id="manual-tab" @click="recurringPaymentPaymentMethod = 'MANUAL'">
                                    Pay By Check
                                </v-tab>
                            </v-tabs>

                            <v-tabs-items v-model="recurringPaymentTab">
                                <v-tab-item>
                                    <v-row class="mt-0">
                                        <v-col class="py-0">
                                            <v-radio-group
                                                v-model="recurringPaymentCardApplication.bankCardType"
                                                :column="false"
                                                class="mt-2"
                                            >
                                                <v-radio
                                                    v-if="isRIC"
                                                    label="Credit"
                                                    value="CREDIT"
                                                    class="pr-5"
                                                    color="secondary"
                                                    @click="setPreFill('CREDIT')"
                                                ></v-radio>
                                                <v-radio
                                                    label="Debit"
                                                    value="DEBIT"
                                                    class="pr-5"
                                                    color="secondary"
                                                    @click="setPreFill('DEBIT')"
                                                ></v-radio>
                                                <v-radio
                                                    label="HSA/FSA"
                                                    value="HSA"
                                                    class="pr-5"
                                                    color="secondary"
                                                    @click="setPreFill('HSA')"
                                                ></v-radio>
                                            </v-radio-group>
                                        </v-col>
                                    </v-row>
                                </v-tab-item>
                            </v-tabs-items>
                            <div v-show="recurringPaymentPaymentMethod === 'CARD'">
                                <v-row v-min-children="1" dense>
                                    <v-col v-min-children="1">
                                        <AppField
                                            :application="application"
                                            :field="recurringPaymentFields['bankCardCardholderName']"
                                            label-code="field.bankCardCardholderName"
                                            :required="downPaymentRequired"
                                        />
                                    </v-col>
                                </v-row>
                                <v-row v-min-children="1" dense>
                                    <v-col v-min-children="1" cols="12" sm="9">
                                        <AppField
                                            :mask="bankCardCardNumberMask"
                                            :application="application"
                                            :field="recurringPaymentFields['bankCardCardNumber']"
                                            label-code="field.bankCardCardNumber"
                                            :append-icon="recurringPaymentShowCreditCardNumber ? '$eye' : '$eye'"
                                            :validations="cardNumberValidations"
                                            :required="downPaymentRequired"
                                            @click:append="toggleMask"
                                            @focus="focus"
                                            @blur="blur"
                                            @input="handleRecurringPaymentBankCardChange"
                                        />
                                    </v-col>
                                    <v-col v-min-children="1" class="d-flex align-center items-center">
                                        <DownpaymentSupportedAssociations />
                                    </v-col>
                                </v-row>
                                <v-row v-min-children="1" dense>
                                    <v-col v-min-children="1" cols="6" sm="4">
                                        <AppField
                                            :id="recurringPaymentFields['bankCardExpirationMonth']"
                                            type="select"
                                            :application="application"
                                            :field="recurringPaymentFields['bankCardExpirationMonth']"
                                            label-code="field.bankCardExpirationMonth"
                                            :required="downPaymentRequired"
                                            @input="handleRecurringPaymentBankCardChange"
                                        />
                                    </v-col>
                                    <v-col v-min-children="1" cols="6" sm="4">
                                        <AppField
                                            :id="recurringPaymentFields['bankCardExpirationYear']"
                                            ref="bankCardExpirationYear"
                                            type="select"
                                            :application="application"
                                            :field="recurringPaymentFields['bankCardExpirationYear']"
                                            label-code="field.bankCardExpirationYear"
                                            :required="downPaymentRequired"
                                        />
                                    </v-col>
                                    <v-col v-min-children="1" cols="12" sm="4">
                                        <AppField
                                            type="text"
                                            :mask="zipCodeMask"
                                            :application="application"
                                            :field="recurringPaymentFields['bankCardZipCode']"
                                            :validations="zipCodeValidations"
                                            label-code="field.bankCardZipCode"
                                            :required="downPaymentRequired"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-show="recurringPaymentPaymentMethod === 'CHECKING'">
                                <v-row>
                                    <v-col class="mt-4" cols="12" sm="6">
                                        <AppField
                                            :application="application"
                                            :field="recurringPaymentFields['bankAccountRoutingNumber']"
                                            label-code="field.routingNumber"
                                            type="text"
                                            :mask="routingNumberMask"
                                            :required="true"
                                        />
                                    </v-col>
                                    <v-col class="mt-4" cols="12" sm="6">
                                        <AppField
                                            :application="application"
                                            :field="recurringPaymentFields['bankAccountAccountNumber']"
                                            type="text"
                                            :mask="bankAccountNumberMask"
                                            label-code="field.accountNumber"
                                            :required="true"
                                        />
                                    </v-col>
                                </v-row>
                                <v-row>
                                    <v-col>
                                        <AppField
                                            :application="application"
                                            :field="recurringPaymentFields['bankAccountBankName']"
                                            type="text"
                                            label-code="field.bankName"
                                            :required="true"
                                        />
                                    </v-col>
                                </v-row>
                            </div>
                            <div v-show="recurringPaymentPaymentMethod === 'MANUAL'">
                                <v-row class="ma-4 ml-0">
                                    <v-col class="pl-1">
                                        <h4 class="mb-4"></h4>
                                        <p>
                                            To pay by check, please make your check <u>payable to</u>
                                            <b>&nbsp;Healthcare Finance Direct</b>
                                        </p>
                                        <p>Mail to: <b>HFD 1707 Eye St, Suite 300, Bakersfield, CA 93301</b></p>
                                        <p>Please put in <u>Memo Section:</u> Account Name and phone number</p>
                                        <p>
                                            <i>
                                                Note: Given processing and mailing times, please mail 2 weeks ahead of
                                                your due date to avoid receiving late payment notifications. If your
                                                payment is late, you may incur a late fee.
                                            </i>
                                        </p>
                                    </v-col>
                                </v-row>
                            </div>
                            <v-row v-show="recurringPaymentPaymentMethod !== 'MANUAL'">
                                <div>
                                    <p style="font-size: smaller; font-weight: lighter; margin-left: 10px">
                                        * By selecting card or checking account you agree to use this payment method for
                                        recurring payments. To opt-out, please refer to your loan agreement.
                                    </p>
                                </div>
                            </v-row>
                            <v-row v-show="recurringPaymentPaymentMethod === 'MANUAL'">
                                <CheckboxField v-model="manualPaymentCheckBox">
                                    By checking this box and clicking "Continue" below, you agree to make your check
                                    payable to Healthcare Finance Direct
                                </CheckboxField>
                            </v-row>
                            <v-card-actions class="pb-12 px-0">
                                <v-row justify="space-between">
                                    <v-col class="d-flex justify-start">
                                        <BackButton class="mr-4 mb-5 ml-0" color="primary" rounded>
                                            {{ $t("action.back") }}
                                        </BackButton>
                                    </v-col>
                                    <v-col class="d-flex flex-column justify-end align-end">
                                        <SubmitButton
                                            rounded
                                            class="secondary submit-button"
                                            :disabled="submitDisabled()"
                                            :submitting="submitting"
                                            @click="submit"
                                        >
                                            {{ $t("action.continue") }}
                                        </SubmitButton>
                                        <div style="font-size: smaller; font-weight: lighter" class="pt-1 text-right">
                                            Continue to review your agreement
                                        </div>
                                    </v-col>
                                </v-row>
                            </v-card-actions>
                        </Form>
                    </v-col>
                </v-row>
                <HfdBankCardErrorModal
                    v-model="showBankCardErrorModal"
                    :error-retry-message="errorRetryMessage"
                    @close-modal="closeBankCardErrorModal"
                >
                </HfdBankCardErrorModal>
                <HfdPromoOfferErrorModal
                    v-model="showSpecialPromoErrorModal"
                    :error-retry-message="errorRetryMessage"
                    @close-modal="closePromoErrorModal"
                >
                </HfdPromoOfferErrorModal>
                <ResponsiveImage :image-url="footerLogo" width="100px" class="footer-logo-center" />
            </v-sheet>
        </v-container>
    </div>
</template>

<script>
    import mask from "@/util/mask"
    import { applicationState, fieldState, sessionState, providerState } from "@/state"
    import { indexBy } from "@/util/array"
    import { mapState } from "vuex"
    import Providers from "@/model/provider/Providers"
    import DynamicProviderKey from "@/model/provider/DynamicProviderKey"
    import HfdBankCardErrorModal from "@/components/provider/hfd/HfdBankCardErrorModal.vue"
    import { workflowService, applyService } from "@/service"
    import { formatCurrency } from "@/util/currency"
    import HFDSubmitBankCardAction from "@/action/actions/provider/HFDSubmitBankCardAction"
    import ProviderState from "@/state/ProviderState"
    import HFDSubmitBankAccountAction from "@/action/actions/provider/HFDSubmitBankAccountAction"
    import ProviderKey from "@/model/provider/ProviderKey"
    import { goto } from "@/util/navigation"
    import PaymentMethodPurpose from "@/model/provider/PaymentMethodPurpose"
    import ProductType from "@/model/provider/ProductType"
    import { isEmpty } from "lodash"
    import { retrieveAcceptOfferError } from "@/util/acceptOfferError"
    import HfdPromoOfferErrorModal from "@/components/provider/hfd/HfdPromoOfferErrorModal.vue"
    import SafeHtmlRenderer from "@/util/safeHtmlRenderer"

    export default {
        name: "HfdDownpaymentCollectionFormView",

        components: {
            HfdPromoOfferErrorModal,
            HfdBankCardErrorModal,
            SafeHtmlRenderer,
        },

        data() {
            const selectedPaymentPlan = applicationState.selectedPaymentPlan
            const allPaymentPlans = providerState.getPaymentPlans(sessionState.providerKey)
            const availablePromoPaymentPlan = allPaymentPlans?.find(
                plan => plan?.typeId === "promo" && plan.providerPlanId === selectedPaymentPlan.providerPlanId
            )
            const specialOfferHeader = selectedPaymentPlan?.terms[0]?.body || ""
            const displayPromoCard = availablePromoPaymentPlan && selectedPaymentPlan?.typeId !== "promo"
            const specialPromotionalSelectDefaultValue = selectedPaymentPlan?.typeId === "promo"

            return {
                applicationStatus: null,
                /** special promo card is enabled if the selected payment plan has terms at time of initialization. */
                displayPromoCard,
                specialPromotionalSelect: specialPromotionalSelectDefaultValue,
                availablePromoPaymentPlan,
                specialOfferHeader,
                acceptOfferError: undefined,
                autoShow: true,
                bankAccountNumberMask: mask.bankAccountNumber,
                bankCardTypes: ["Credit", "Debit", "HSA"],
                bankCardCardNumberMask: {
                    mask: mask.hiddenCreditCardNumberMask,
                    definitions: {
                        x: mask.star,
                    },
                },
                isVertical: false,
                manualPaymentCheckBox: false,
                messages: {
                    en: {
                        rule: {
                            validateExpirationMonthCheck: "Please check the month, it is no longer in this year",
                            validateExpiration: "Invalid Month",
                        },
                    },
                },
                providerKey: ProviderKey.HFD_STANDARD,
                providerInfo: Providers[DynamicProviderKey.extractProviderKey(sessionState.providerKey)],
                promotionalApr: availablePromoPaymentPlan?.promotionalApr,
                promotionalMonthlyAmountCents: formatCurrency(availablePromoPaymentPlan?.promotionalMonthlyAmountCents),
                promotionalTermMonths: availablePromoPaymentPlan?.promotionalTermMonths,
                promotionalTotalNumberOfPayments: availablePromoPaymentPlan?.promotionalTotalNumberOfPayments,
                recurringMonthlyTerms: selectedPaymentPlan.termMonths,
                recurringPaymentFields: indexBy(fieldState.fields, "id"),
                recurringPaymentShowCreditCardNumber: false,
                recurringPaymentAmountFormatted: formatCurrency(selectedPaymentPlan.monthlyAmountCents),
                recurringPaymentCardApplication: {
                    bankCardType: "DEBIT",
                },
                recurringPaymentFormSettings: {
                    readOnly: false,
                },
                recurringPaymentPaymentMethod: "CARD",
                recurringPaymentBankCardErrorMessage: "",
                recurringPaymentTab: "CARD",
                regularApr: selectedPaymentPlan?.apr,
                routingNumberMask: mask.routingNumber,

                showBankCardErrorModal: false,
                showSpecialPromoErrorModal: false,
                submitting: false,
                validations: {
                    bankCardExpirationYear: {
                        validateExpirationMonthCheck: function (value, parentVm) {
                            const d = new Date()
                            let currentMonth = d.getMonth() + 1
                            let currentYear = d.getFullYear()
                            if (currentYear.toString() === value) {
                                return currentMonth <= parseInt(parentVm.bankCardExpirationMonth)
                            } else {
                                return true
                            }
                        },
                    },
                    bankCardExpirationMonth: {
                        validateExpiration(value, parentVm) {
                            const d = new Date()
                            let currentMonth = d.getMonth() + 1
                            let currentYear = d.getFullYear()
                            if (currentYear.toString() === parentVm.bankCardExpirationYear) {
                                return currentMonth <= value
                            } else {
                                return true
                            }
                        },
                    },
                },
                years: [],
                zipCodeMask: mask.zipCode,
            }
        },

        computed: {
            ...mapState("application", {
                application: state => state.data,
            }),

            cardNumberValidations() {
                return {
                    invalid: value => this.validatePAN(value),
                }
            },

            headerDescriptionLanguage() {
                return this.displayPromoCard ? "promo" : "regular"
            },

            currentPayment() {
                return `Current Payment: ${this.totalNumberOfPayments} Payments Of ${this.frequencyDependentPaymentAmount} @ ${this.regularApr}% APR`
            },

            specialPromo() {
                return `Promotional Payoff Payment:  ${this.promotionalTotalNumberOfPayments} Payments Of ${this.promotionalMonthlyAmountCents} @ ${this.promotionalApr}% APR`
            },

            frequencyDependentPaymentAmount() {
                return formatCurrency(applicationState.selectedPaymentPlan.paymentAmountCents)
            },

            downPaymentRequired() {
                return applicationState.selectedPaymentPlan.downPaymentCents !== 0
            },

            termMonths() {
                return applicationState.selectedPaymentPlan.termMonths
            },

            payFrequency() {
                return applicationState.selectedPaymentPlan.payFrequency
            },

            totalNumberOfPayments() {
                return applicationState.selectedPaymentPlan.totalNumberOfPayments
            },

            footerLogo() {
                const response = ProviderState.getPreAuthResponse()
                return Providers[ProviderKey.HFD_STANDARD]?.footerLogo[response.providerProductName]
            },

            errorRetryMessage() {
                return this.recurringPaymentBankCardErrorMessage
            },

            isRIC() {
                return ProviderState.getPreAuthResponse().providerProductName === ProductType.RIC
            },

            zipCodeValidations() {
                return {
                    invalid: value => this.validateZipCode(value),
                }
            },
        },

        async mounted() {
            if (window.innerWidth <= 600) {
                this.isVertical = true
            } else {
                this.isVertical = false
            }

            const applicationStatusResponse = await applyService.getApplicationStatus(sessionState.providerKey)
            this.applicationStatus = applicationStatusResponse?.providerStatus

            const downPaymentBankCard = applicationState.application.bankCard
            const downPaymentMethod = downPaymentBankCard.downPaymentMethod
            if (!this.recurringPaymentCardApplication) {
                this.recurringPaymentCardApplication = {}
            }
            if (!this.isRIC && downPaymentMethod === "CREDIT") {
                this.recurringPaymentCardApplication.bankCardType = "DEBIT"
                applicationState.application.bankCard = {}
            } else {
                this.recurringPaymentCardApplication.bankCardType = downPaymentBankCard.downPaymentMethod || "DEBIT"
            }
        },

        created() {
            this.acceptOfferError = retrieveAcceptOfferError()
        },

        methods: {
            focus() {
                if (this.autoShow) {
                    setTimeout(() => {
                        this.setCreditCardVisibility(true)
                    })
                }
            },

            submitDisabled() {
                if (this.recurringPaymentPaymentMethod === "MANUAL") {
                    return !this.manualPaymentCheckBox
                } else if (this.recurringPaymentPaymentMethod === "CARD") {
                    return !this.validateCard(this.application.bankCard)
                } else {
                    return !this.validateACH(this.application.bankAccount)
                }
            },

            handleRecurringPaymentBankCardChange() {
                const d = new Date()
                let currentYear = d.getFullYear()
                if (currentYear.toString() === this.application.bankCard.expirationYear) {
                    setTimeout(() => {
                        this.$refs.bankCardExpirationYear.validate()
                    })
                }

                setTimeout(() => {
                    this.bankCardCardNumberMask = {
                        mask: mask.getBankCardMask(
                            this.application.bankCard?.cardNumber,
                            this.recurringPaymentShowCreditCardNumber
                        ),
                    }
                })
            },

            blur() {
                if (this.autoShow) {
                    setTimeout(() => {
                        this.setCreditCardVisibility(false)
                    })
                }
            },

            setCreditCardVisibility(flag) {
                this.recurringPaymentShowCreditCardNumber = flag
                this.bankCardCardNumberMask = {
                    mask: mask.getBankCardMask(
                        this.application.bankCard?.cardNumber,
                        this.recurringPaymentShowCreditCardNumber
                    ),
                }
            },

            toggleMask() {
                this.setCreditCardVisibility(!this.recurringPaymentShowCreditCardNumber)
            },

            setPreFill(cardType) {
                let downPaymentBankCard = applicationState.application.downPaymentBankCard
                if (cardType !== downPaymentBankCard.downPaymentMethod) {
                    const recurringPaymentBankCard = this.application.bankCard
                    recurringPaymentBankCard.cardNumber = undefined
                    recurringPaymentBankCard.cardholderName = undefined
                    recurringPaymentBankCard.expirationMonth = undefined
                    recurringPaymentBankCard.expirationYear = undefined
                    recurringPaymentBankCard.zipCode = undefined
                } else {
                    const recurringPaymentBankCard = this.application.bankCard
                    recurringPaymentBankCard.cardNumber = downPaymentBankCard.cardNumber
                    recurringPaymentBankCard.cardholderName = downPaymentBankCard.cardholderName
                    recurringPaymentBankCard.expirationMonth = downPaymentBankCard.expirationMonth
                    recurringPaymentBankCard.expirationYear = downPaymentBankCard.expirationYear
                    recurringPaymentBankCard.zipCode = downPaymentBankCard.zipCode
                }
            },

            async submit() {
                try {
                    this.submitting = true
                    let path = "terms"
                    if (this.acceptOfferError) {
                        path = "acceptOfferSubmit"
                    }

                    if (this.recurringPaymentPaymentMethod === "CARD") {
                        const recurringPaymentBankCard = this.application.bankCard
                        const recurringPaymentBankCardRequest = {
                            type: this.recurringPaymentCardApplication.bankCardType,
                            cardNumber: recurringPaymentBankCard?.cardNumber,
                            cardholderName: recurringPaymentBankCard?.cardholderName,
                            expirationMonth: recurringPaymentBankCard?.expirationMonth,
                            expirationYear: recurringPaymentBankCard?.expirationYear,
                            zipCode: recurringPaymentBankCard?.zipCode,
                            automaticPayments: true,
                            primary: true,
                            purpose: PaymentMethodPurpose.LOAN_PAYMENT,
                        }

                        if (this.specialPromotionalSelect && this.applicationStatus === "approved") {
                            await this.optInToPromoPayments(
                                applicationState.selectedPaymentPlan,
                                this.availablePromoPaymentPlan
                            ).then(_ => {
                                workflowService
                                    .invoke(
                                        new HFDSubmitBankCardAction(
                                            sessionState.providerKey,
                                            recurringPaymentBankCardRequest,
                                            path
                                        )
                                    )
                                    .then(submitBankCardResponse => {
                                        return this.handleHFDSubmitBankAccountActionErrors(submitBankCardResponse)
                                    })
                            })
                        } else {
                            await workflowService
                                .invoke(
                                    new HFDSubmitBankCardAction(
                                        sessionState.providerKey,
                                        recurringPaymentBankCardRequest,
                                        path
                                    )
                                )
                                .then(submitBankCardResponse => {
                                    return this.handleHFDSubmitBankAccountActionErrors(submitBankCardResponse)
                                })
                        }
                    } else if (this.recurringPaymentPaymentMethod === "CHECKING") {
                        const recurringPaymentBankAccount = this.application.bankAccount
                        const recurringPaymentBankAccountRequest = {
                            type: "CHECKING",
                            accountNumber: recurringPaymentBankAccount?.accountNumber,
                            routingNumber: recurringPaymentBankAccount?.routingNumber,
                            bankName: recurringPaymentBankAccount?.bankName,
                            automaticPayments: true,
                            primary: true,
                            purpose: PaymentMethodPurpose.LOAN_PAYMENT,
                        }
                        if (this.specialPromotionalSelect && this.applicationStatus === "approved") {
                            await this.optInToPromoPayments(
                                applicationState.selectedPaymentPlan,
                                this.availablePromoPaymentPlan
                            ).then(_ => {
                                workflowService
                                    .invoke(
                                        new HFDSubmitBankAccountAction(
                                            sessionState.providerKey,
                                            recurringPaymentBankAccountRequest,
                                            path
                                        )
                                    )
                                    .then(submitBankAccountResponse => {
                                        return this.handleHFDSubmitBankAccountActionErrors(submitBankAccountResponse)
                                    })
                            })
                        } else {
                            await workflowService
                                .invoke(
                                    new HFDSubmitBankAccountAction(
                                        sessionState.providerKey,
                                        recurringPaymentBankAccountRequest,
                                        path
                                    )
                                )
                                .then(submitBankAccountResponse => {
                                    return this.handleHFDSubmitBankAccountActionErrors(submitBankAccountResponse)
                                })
                        }
                    } else {
                        if (this.specialPromotionalSelect && this.applicationStatus === "approved") {
                            const selectedPaymentPlan = applicationState.selectedPaymentPlan
                            await applyService.confirmPaymentPlan(sessionState.providerKey, {
                                providerPlanCode: selectedPaymentPlan?.providerPlanCode,
                                promotionalName: selectedPaymentPlan?.promotionalName,
                            })
                        }

                        await goto({ path: `/providers/${sessionState.providerKey}/${path}` })
                    }
                } finally {
                    this.submitting = false
                }
            },

            async optInToPromoPayments(selectedPlan, promoPlan) {
                return applyService.selectPaymentPlan(selectedPlan.providerKey, {
                    providerPlanCode: selectedPlan?.providerPlanCode,
                    financeAmountCents: applicationState.application.tentativePurchaseAmountCents,
                    promotionalName: promoPlan?.promotionalName,
                })
            },

            async handleHFDSubmitBankAccountActionErrors(recurringPaymentResponse) {
                if (
                    this.specialPromotionalSelect &&
                    recurringPaymentResponse.responseCode === "error" &&
                    recurringPaymentResponse?.errors === null
                ) {
                    this.showSpecialPromoErrorModal = true
                } else if (recurringPaymentResponse?.errors?.length > 0) {
                    this.recurringPaymentBankCardErrorMessage = recurringPaymentResponse.errors[0].errorMessage
                    this.showBankCardErrorModal = true
                }
            },

            validatePAN(pan) {
                if (!pan?.length) {
                    return true
                }

                if (/[^0-9-\s]+/.test(pan)) {
                    return false
                }

                const association = pan.substring(0, 1)
                switch (association) {
                    case "3":
                        return this.validateAmex(pan)
                    case "4":
                        return this.validateVisa(pan)
                    case "5":
                        return this.validateMastercard(pan)
                    case "6":
                        return this.validateDiscover(pan)
                    default:
                        return false
                }
            },

            validateCard(bankCard) {
                return (
                    bankCard !== undefined &&
                    bankCard.cardNumber !== undefined &&
                    this.validatePAN(bankCard.cardNumber) &&
                    bankCard.cardholderName !== undefined &&
                    !isEmpty(bankCard.expirationMonth) &&
                    !isEmpty(bankCard.expirationYear) &&
                    bankCard.zipCode !== undefined &&
                    this.validateZipCode(bankCard.zipCode)
                )
            },

            validateACH(bankAccount) {
                return (
                    !!bankAccount &&
                    !!bankAccount.accountNumber &&
                    bankAccount.accountNumber.length >= 5 &&
                    bankAccount.accountNumber.length <= 17 &&
                    !!bankAccount.bankName &&
                    bankAccount.bankName.length > 0 &&
                    !!bankAccount.routingNumber &&
                    bankAccount.routingNumber.length === 9
                )
            },

            validateVisa(pan) {
                return pan.length === 16 && this.luhn(pan)
            },

            validateAmex(pan) {
                return pan.length === 15 && this.luhn(pan)
            },

            validateMastercard(pan) {
                return pan.length === 16 && this.luhn(pan)
            },

            validateDiscover(pan) {
                return pan.length === 16 && this.luhn(pan)
            },

            validateZipCode(zipcode) {
                return zipcode.length === 5
            },

            closeBankCardErrorModal() {
                this.showBankCardErrorModal = false
            },

            closePromoErrorModal() {
                this.showSpecialPromoErrorModal = false
            },

            luhn(pan) {
                let nCheck = 0
                let nDigit = 0
                let bEven = false

                for (let n = pan.length - 1; n >= 0; n--) {
                    let cDigit = pan.charAt(n)
                    nDigit = parseInt(cDigit, 10)

                    if (bEven) {
                        if ((nDigit *= 2) > 9) nDigit -= 9
                    }

                    nCheck += nDigit
                    bEven = !bEven
                }

                return nCheck % 10 === 0
            },
        },
    }
</script>
<style lang="scss" scoped>
    #special-promo-container {
        margin: 20px 0;
        padding: 4px;
        border-radius: 4px;
        border: 1px solid $grey-base;
        background: $info-color-tint;
    }

    .submit-button {
        height: fit-content !important;
        min-width: 160px;
        white-space: normal;
        display: block;

        @media #{map-get($display-breakpoints, "sm-and-up")} {
            display: inline-flex;
            white-space: nowrap;
            width: auto;
            height: auto;
        }
    }

    .terms-check-container {
        @media #{map-get($display-breakpoints, "sm-and-up")} {
            margin-top: -20px;
        }
    }

    h4 {
        font-weight: 600 !important;
        font-size: to_rem(22px) !important;
        line-height: to_rem(28px) !important;
        color: $primary-color;
    }
</style>

<style lang="scss">
    // this class needs to override classes that are set by vuetify, so we need it to be global here.
    .promo-select-label {
        .v-label {
            color: rgba(0, 0, 0, 0.76) !important;
            font-weight: 600;
        }
    }
    // this class comes down in an html blob from the plan, so it needs to be a global style.
    .special_offer {
        display: inline-block;
        padding-right: 4px;
        font-weight: bold;
    }

    .offer_label {
        font-weight: bold;
    }
</style>
